"use client";

import { Download } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { formatBytes } from "./format";

export interface ProcessedVideo {
  blob: Blob;
  /** Output filename without extension. */
  baseName: string;
  /** File extension (no dot). Defaults to "mp4". */
  extension?: string;
}

/** Result panel for video-producing tools: inline preview + download. */
export function VideoResultPanel({
  result,
  title = "Done",
}: {
  result: ProcessedVideo;
  title?: string;
}) {
  const t = useTranslations("mediaTools.result.video");
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const next = URL.createObjectURL(result.blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [result]);

  const fileName = `${result.baseName}.${result.extension ?? "mp4"}`;

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <p className="font-medium">{title}</p>
      {url && <video src={url} controls className="max-h-80 w-full rounded bg-black" />}
      <Button asChild disabled={!url}>
        <a href={url ?? undefined} download={fileName}>
          <Download className="mr-2 h-4 w-4" />
          {t("download", { fileName, size: formatBytes(result.blob.size) })}
        </a>
      </Button>
    </div>
  );
}
